import React, { useEffect, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';

import { Alerta } from '../../components/alerta';
import DayRangePicker from '../../components/dayRangePicker';
import { FunnelPanel } from '../../components/funnelPanel';
import LineChart from '../../components/lineChart';
import MarketingKpiCard from '../../components/marketingKpiCard';
import MultiSelectFilter from '../../components/multiSelectFilter';
import {
    compactCurrency,
    formatCurrency
} from '../../utils/stringFormat';
import { formatInteger } from '../../utils/numberUtils';
import { formatDatePtBr } from '../../utils/dateUtils';
import {
    applyMarketingFilters,
    buildChannelTotals,
    buildComparisonChartRows,
    buildComparisonRange,
    buildDelta,
    buildDimensionOptions,
    buildMarketingDashboardFilters,
    buildMarketingFunnelData,
    buildMarketingTotals,
    buildOriginOptions,
    costPer,
    fetchMarketingDashboardData,
    MARKETING_DIMENSIONS,
    pruneMarketingFilters
} from './marketingDashboardData';
import {
    buildDashboardDateRange,
    CRM_DASHBOARD_MIN_DATE
} from './crmDashboardData';
import '../css/crmDashboard.css';

const allOption = 'all';

const MAX_X_TICKS = 10;

const formatRange = ({ startDate, endDate }) => `${formatDatePtBr(startDate)} – ${formatDatePtBr(endDate)}`;

const formatCostPer = (value) => (value === null ? '—' : formatCurrency(value));

// CPL e CPA: menor é melhor, então a seta verde aponta para baixo.
const KPI_DEFINITIONS = [
    {
        label: 'Leads',
        accent: '#e15e5e',
        icon: <PersonAddAltRoundedIcon sx={{ fontSize: 18 }} />,
        measure: (totals) => totals.leads,
        format: (value) => formatInteger(value)
    },
    {
        label: 'CPL',
        accent: '#7b8eec',
        icon: <ReceiptLongRoundedIcon sx={{ fontSize: 18 }} />,
        lowerIsBetter: true,
        measure: (totals) => costPer(totals.cost, totals.leads),
        format: formatCostPer
    },
    {
        label: 'Vendas',
        accent: '#2ab47d',
        icon: <EmojiEventsRoundedIcon sx={{ fontSize: 18 }} />,
        measure: (totals) => totals.sales,
        format: (value) => formatInteger(value)
    },
    {
        label: 'CPA',
        accent: 'var(--setta-amber)',
        icon: <CampaignRoundedIcon sx={{ fontSize: 18 }} />,
        lowerIsBetter: true,
        measure: (totals) => costPer(totals.cost, totals.sales),
        format: formatCostPer
    },
    {
        label: 'Valor gasto',
        accent: 'var(--app-text)',
        icon: <PaidRoundedIcon sx={{ fontSize: 18 }} />,
        measure: (totals) => totals.cost,
        format: (value) => compactCurrency(value)
    }
];

const MarketingDashboardHero = () => (
    <div className='crm-dashboard-hero'>
        <h1 className='crm-dashboard-title'>Funil de Marketing</h1>
        <p className='crm-dashboard-body crm-dashboard-hero-copy'>
            Aquisição, mídia e conversão até vendas, por campanha, conjunto e anúncio.
        </p>
    </div>
);

const MarketingDashboardFilters = ({
    comparisonRange,
    dateRange,
    dimensionOptions,
    filters,
    originOptions,
    onComparisonApply,
    onComparisonReset,
    onDateRangeApply,
    onDateRangeReset,
    onFilterChange
}) => (
    <Paper className='crm-dashboard-filter-shell crm-marketing-dashboard-filter-shell'>
        <div className='crm-dashboard-filter-row crm-marketing-dashboard-filter-row has-period-pair'>
            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='marketing-origem-filter-label'
                label='Origem'
                value={filters.origem_id}
                options={[{ value: allOption, label: 'Todas as Origens' }, ...originOptions]}
                allOptionValue={allOption}
                onChange={(values) => onFilterChange('origem_id', values.includes(allOption) ? [] : values)}
            />

            {MARKETING_DIMENSIONS.map((dimension) => (
                <MultiSelectFilter
                    key={dimension.filterKey}
                    className='crm-dashboard-filter-control'
                    labelId={`marketing-${dimension.filterKey}-filter-label`}
                    label={dimension.label}
                    value={filters[dimension.filterKey]}
                    options={[{ value: allOption, label: dimension.allLabel }, ...dimensionOptions[dimension.filterKey]]}
                    allOptionValue={allOption}
                    onChange={(values) => onFilterChange(dimension.filterKey, values.includes(allOption) ? [] : values)}
                />
            ))}

            <div className='crm-dashboard-range-field'>
                <span className='crm-dashboard-range-label'>Analisando</span>
                <DayRangePicker
                    ariaLabel='Período analisado no dashboard de marketing'
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={CRM_DASHBOARD_MIN_DATE}
                    maxDate={new Date()}
                    onApply={onDateRangeApply}
                    onClear={onDateRangeReset}
                />
            </div>

            <div className='crm-dashboard-range-field'>
                <span className='crm-dashboard-range-label'>Comparando com</span>
                <DayRangePicker
                    ariaLabel='Período de comparação no dashboard de marketing'
                    startDate={comparisonRange.startDate}
                    endDate={comparisonRange.endDate}
                    minDate={CRM_DASHBOARD_MIN_DATE}
                    maxDate={new Date()}
                    onApply={onComparisonApply}
                    onClear={onComparisonReset}
                />
            </div>
        </div>
    </Paper>
);

const MarketingDashboardSummary = ({ comparisonLabel, comparisonRows, rows }) => {
    const totals = buildMarketingTotals(rows);
    const comparisonTotals = buildMarketingTotals(comparisonRows);
    const channels = buildChannelTotals(rows);
    const comparisonChannels = buildChannelTotals(comparisonRows);

    return (
        <div className='crm-dashboard-summary-grid'>
            {KPI_DEFINITIONS.map((kpi) => (
                <MarketingKpiCard
                    key={kpi.label}
                    accent={kpi.accent}
                    icon={kpi.icon}
                    label={kpi.label}
                    value={kpi.format(kpi.measure(totals))}
                    delta={buildDelta(kpi.measure(totals), kpi.measure(comparisonTotals), kpi.lowerIsBetter)}
                    comparisonLabel={comparisonLabel}
                    splits={channels.map((channel, index) => ({
                        label: channel.label,
                        color: channel.color,
                        value: kpi.format(kpi.measure(channel.totals)),
                        delta: buildDelta(
                            kpi.measure(channel.totals),
                            kpi.measure(comparisonChannels[index].totals),
                            kpi.lowerIsBetter
                        )
                    }))}
                />
            ))}
        </div>
    );
};

const MarketingDailyChart = ({ comparisonLabel, currentLabel, eyebrow, rows, title }) => {
    const series = [
        {
            key: 'atual',
            label: currentLabel,
            peakText: (point) => `Pico: ${formatInteger(point.atual)} em ${formatDatePtBr(point.day)}`,
            tooltipValue: (point) => `${formatInteger(point.atual)} em ${formatDatePtBr(point.day)}`
        },
        {
            key: 'comparacao',
            label: comparisonLabel,
            color: 'var(--app-muted)',
            dashed: true,
            peakText: (point) => `Pico comparado: ${formatInteger(point.comparacao)}`,
            tooltipValue: (point) => (point.comparisonDay
                ? `${formatInteger(point.comparacao)} em ${formatDatePtBr(point.comparisonDay)}`
                : 'sem dia correspondente')
        }
    ];

    return (
        <LineChart
            ariaLabel={title}
            eyebrow={eyebrow}
            icon={ShowChartRoundedIcon}
            maxXTicks={MAX_X_TICKS}
            pointAriaLabel={(point) => `${formatDatePtBr(point.day)}: ${series.map((serie) => serie.tooltipValue(point)).join(', ')}`}
            pointKey={(point) => point.key}
            rows={rows.map((row, index) => ({ ...row, label: `Dia ${index + 1} de cada período` }))}
            series={series}
            title={title}
            xTickLabel={(point) => formatDatePtBr(point.day).slice(0, 5)}
        />
    );
};

const MarketingDashboard = () => {
    const [dateRange, setDateRange] = useState(buildDashboardDateRange);
    const [comparisonRange, setComparisonRange] = useState(() => buildComparisonRange(buildDashboardDateRange()));
    const [filters, setFilters] = useState(buildMarketingDashboardFilters);
    const [sourceData, setSourceData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        let ignore = false;

        const loadDashboard = async () => {
            setIsLoading(true);

            try {
                const nextSourceData = await fetchMarketingDashboardData({ dateRange, comparisonRange });
                if (!ignore) {
                    setSourceData(nextSourceData);
                    setFilters((prev) => pruneMarketingFilters(nextSourceData.rows, prev));
                }
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadDashboard();
        return () => { ignore = true; };
    }, [dateRange, comparisonRange]);

    const rows = sourceData ? applyMarketingFilters(sourceData.rows, filters) : [];
    const comparisonRows = sourceData ? applyMarketingFilters(sourceData.comparisonRows, filters) : [];
    const dimensionOptions = Object.fromEntries(MARKETING_DIMENSIONS.map(({ filterKey }) => [
        filterKey,
        sourceData ? buildDimensionOptions(sourceData.rows, filters, filterKey) : []
    ]));
    const currentLabel = formatRange(dateRange);
    const comparisonLabel = formatRange(comparisonRange);

    // Trocar o período analisado leva a comparação junto para o mesmo trecho do mês anterior.
    const handleDateRangeApply = (startDate, endDate) => {
        const nextRange = { startDate, endDate };
        setDateRange(nextRange);
        setComparisonRange(buildComparisonRange(nextRange));
    };

    // O recorte é obrigatório na query: limpar volta ao período padrão em vez de esvaziar.
    const handleDateRangeReset = () => {
        const defaultRange = buildDashboardDateRange();
        setDateRange(defaultRange);
        setComparisonRange(buildComparisonRange(defaultRange));
    };

    const handleFilterChange = (filterKey, values) => {
        setFilters((prev) => {
            const nextFilters = { ...prev, [filterKey]: values };
            return sourceData ? pruneMarketingFilters(sourceData.rows, nextFilters) : nextFilters;
        });
    };

    return (
        <Box className='mainBox crm-dashboard-page crm-marketing-dashboard-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-dashboard-frame'>
                <div className='crm-dashboard-content'>
                    <div className='crm-dashboard-topbar'>
                        <MarketingDashboardHero />
                        <MarketingDashboardFilters
                            comparisonRange={comparisonRange}
                            dateRange={dateRange}
                            dimensionOptions={dimensionOptions}
                            filters={filters}
                            originOptions={sourceData ? buildOriginOptions(sourceData.rows) : []}
                            onComparisonApply={(startDate, endDate) => setComparisonRange({ startDate, endDate })}
                            onComparisonReset={() => setComparisonRange(buildComparisonRange(dateRange))}
                            onDateRangeApply={handleDateRangeApply}
                            onDateRangeReset={handleDateRangeReset}
                            onFilterChange={handleFilterChange}
                        />
                    </div>

                    {isLoading && <LinearProgress className='crm-dashboard-loading' variant='indeterminate' />}

                    {sourceData && (
                        <>
                            <MarketingDashboardSummary
                                comparisonLabel={comparisonLabel}
                                comparisonRows={comparisonRows}
                                rows={rows}
                            />

                            <div className='marketing-dashboard-charts'>
                                <MarketingDailyChart
                                    comparisonLabel={comparisonLabel}
                                    currentLabel={currentLabel}
                                    eyebrow='Volume · CRM'
                                    rows={buildComparisonChartRows({ comparisonRange, comparisonRows, dateRange, field: 'leads', rows })}
                                    title='Leads por dia'
                                />
                                <MarketingDailyChart
                                    comparisonLabel={comparisonLabel}
                                    currentLabel={currentLabel}
                                    eyebrow='Fechamento · CRM'
                                    rows={buildComparisonChartRows({ comparisonRange, comparisonRows, dateRange, field: 'vendas', rows })}
                                    title='Vendas por dia'
                                />
                            </div>

                            <div className='crm-dashboard-panels crm-marketing-dashboard-panels'>
                                <FunnelPanel
                                    funnelData={buildMarketingFunnelData(rows)}
                                    title='Funil de Marketing'
                                    subtitle='Da mídia paga até a conversão em cliente'
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Box>
    );
};

export default MarketingDashboard;
